/**
 * Club Startup Routing — Phase 4A
 *
 * Centralized helper for resolving whether a user should be routed to
 * Club Home or Club Setup on app launch.
 *
 * Doctrine: DUEL_CLUB_MODE_ARCHITECTURE.md
 * - `organizations` is the canonical table for Club / School Mode.
 * - `gyms` is NOT touched by this module.
 *
 * Usage:
 *   Called once per session from gate.tsx after auth + profile are confirmed.
 *   Returns an action: 'none' | 'setup' | 'home'
 *   Non-club users always get action: 'none' — existing flow is unchanged.
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import { reconcileClubOrg, resolveClubOwnerOrg, reconcileCoachOrg, resolveCoachOwnerOrg } from '@/lib/orgProvisioning';
import { getOrgRoleForUser } from '@/lib/organizations';

const log = createLogger('CLUB_STARTUP');

/** Entitlement values that qualify as club/org_pro subscriptions */
const CLUB_TIERS = new Set([
  'org_pro',
  'club_starter',
  'club_growth',
  'club_performance',
  'club_elite',
]);

/** Roles that get routed to Club Home / Club Setup (not plain members) */
const STAFF_ROLES = new Set(['owner', 'admin', 'coach']);

export interface ClubStartupResult {
  /** What the startup router should do */
  action: 'none' | 'setup' | 'home';
  /** The org's UUID, or null if no org found */
  orgId: string | null;
  /** The user's role in that org, or null */
  role: string | null;
}

const NONE: ClubStartupResult = { action: 'none', orgId: null, role: null };

/**
 * Resolve the club startup action for an authenticated user.
 *
 * Must only be called AFTER:
 *   - Auth session is confirmed (user is authenticated)
 *   - Profile (including subscription_tier) is available
 *   - Onboarding is confirmed complete
 *
 * Returns NONE for all non-club users — existing routing is untouched.
 *
 * Entire function is wrapped in try/catch: any error returns NONE.
 * Never throws; never crashes startup.
 */
export async function resolveClubStartup(
  userId: string,
  subscriptionTier: string | null | undefined,
): Promise<ClubStartupResult> {
  try {
    // ── Step 1: gate on subscription tier ──────────────────────────────────
    if (!subscriptionTier || !CLUB_TIERS.has(subscriptionTier)) {
      log.info('[CLUB_STARTUP] Not a club tier, skipping', { subscriptionTier });
      return NONE;
    }

    log.info('[CLUB_STARTUP] Club tier detected, running reconciliation', {
      userId: userId.slice(0, 8),
      subscriptionTier,
    });

    // ── Step 2: reconcile (provision org if missing — idempotent) ──────────
    try {
      await reconcileClubOrg(userId, subscriptionTier);
    } catch (reconcileErr: any) {
      // Reconciliation failure is non-fatal: org may already exist
      log.warn('[CLUB_STARTUP] reconcileClubOrg threw, continuing', reconcileErr?.message);
    }

    // ── Step 3: resolve the user's owned org ───────────────────────────────
    let orgId: string | null = null;
    try {
      orgId = await resolveClubOwnerOrg(userId);
    } catch (resolveErr: any) {
      log.warn('[CLUB_STARTUP] resolveClubOwnerOrg threw', resolveErr?.message);
      return NONE;
    }

    if (!orgId) {
      log.info('[CLUB_STARTUP] No org found after reconciliation — fallback to none');
      return NONE;
    }

    // ── Step 4: resolve the user's role ────────────────────────────────────
    let role: string | null = null;
    try {
      role = await getOrgRoleForUser(orgId, userId);
    } catch (roleErr: any) {
      log.warn('[CLUB_STARTUP] getOrgRoleForUser threw', roleErr?.message);
      return NONE;
    }

    if (!role || !STAFF_ROLES.has(role)) {
      // Members use the regular app — no club routing
      log.info('[CLUB_STARTUP] Role is not staff, skipping club routing', { role });
      return NONE;
    }

    // ── Step 5: check setup_complete ───────────────────────────────────────
    let setupComplete = false;
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('setup_complete')
        .eq('id', orgId)
        .maybeSingle();

      if (error) {
        log.warn('[CLUB_STARTUP] Failed to fetch org setup_complete', error.message);
        return NONE;
      }

      setupComplete = data?.setup_complete === true;
    } catch (fetchErr: any) {
      log.warn('[CLUB_STARTUP] org fetch threw', fetchErr?.message);
      return NONE;
    }

    // ── Step 6: decide action ──────────────────────────────────────────────
    const action = setupComplete ? 'home' : 'setup';

    log.info('[CLUB_STARTUP] Resolved', { orgId, role, setupComplete, action });

    return { action, orgId, role };
  } catch (err: any) {
    // Outer safety net — never crash startup
    log.error('[CLUB_STARTUP] Unexpected error in resolveClubStartup', err?.message ?? err);
    return NONE;
  }
}

// ─── Coach Startup Routing ─────────────────────────────────────────────────

export interface CoachStartupResult {
  /** What the startup router should do */
  action: 'none' | 'dashboard';
  /** The personal coaching org's UUID, or null */
  orgId: string | null;
}

const COACH_NONE: CoachStartupResult = { action: 'none', orgId: null };

/**
 * Resolve the coach startup action for an authenticated user.
 *
 * Priority note: club routing (org_pro) takes priority over coach routing
 * (coach_pro). gate.tsx MUST call resolveClubStartup first and only call
 * this function if club routing returned action='none'.
 *
 * Must only be called AFTER:
 *   - Auth session is confirmed
 *   - Profile (including subscription_tier) is available
 *   - resolveClubStartup returned action:'none'
 *
 * Returns COACH_NONE for all non-coach_pro users.
 * Never throws; never crashes startup.
 */
export async function resolveCoachStartup(
  userId: string,
  subscriptionTier: string | null | undefined,
): Promise<CoachStartupResult> {
  try {
    // ── Step 1: gate on subscription tier ──────────────────────────────────
    if (!subscriptionTier || subscriptionTier !== 'coach_pro') {
      log.info('[COACH_STARTUP] Not coach_pro tier, skipping', { subscriptionTier });
      return COACH_NONE;
    }

    log.info('[COACH_STARTUP] coach_pro detected, reconciling coach org', {
      userId: userId.slice(0, 8),
    });

    // ── Step 2: reconcile (provision personal coaching org if missing) ──────
    try {
      await reconcileCoachOrg(userId);
    } catch (reconcileErr: any) {
      log.warn('[COACH_STARTUP] reconcileCoachOrg threw, continuing', reconcileErr?.message);
    }

    // ── Step 3: resolve the coach's personal org ───────────────────────────
    let orgId: string | null = null;
    try {
      orgId = await resolveCoachOwnerOrg(userId);
    } catch (resolveErr: any) {
      log.warn('[COACH_STARTUP] resolveCoachOwnerOrg threw', resolveErr?.message);
      return COACH_NONE;
    }

    if (!orgId) {
      log.info('[COACH_STARTUP] No coach org found after reconciliation — fallback to none');
      return COACH_NONE;
    }

    log.info('[COACH_STARTUP] Resolved → dashboard', { orgId });

    return { action: 'dashboard', orgId };
  } catch (err: any) {
    log.error('[COACH_STARTUP] Unexpected error in resolveCoachStartup', err?.message ?? err);
    return COACH_NONE;
  }
}
